import React, { useEffect, useState, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addHours } from 'date-fns';
import { de } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/calendar.css';
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import calDAVService from '../services/calDAVService';
import type { CalDAVEventDto, CalDAVSettingDto } from '../types/api';
import { useNavigate } from 'react-router-dom';

// Setup the localizer for date-fns with German locale
const locales = {
  de: de,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Custom messages in German
const messages = {
  allDay: 'Ganztägig',
  previous: 'Zurück',
  next: 'Weiter',
  today: 'Heute',
  month: 'Monat',
  week: 'Woche',
  day: 'Tag',
  agenda: 'Agenda',
  date: 'Datum',
  time: 'Zeit',
  event: 'Termin',
  noEventsInRange: 'Keine Termine in diesem Zeitraum.',
  showMore: (total: number) => `+ ${total} weitere`,
};

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: CalDAVEventDto;
}

const AppointmentCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalDAVEventDto | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [view, setView] = useState<View>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [calDAVSettings, setCalDAVSettings] = useState<CalDAVSettingDto[]>([]);
  const [selectedSettingId, setSelectedSettingId] = useState<number | null>(null);

  // New event form state
  const [newEvent, setNewEvent] = useState({
    summary: '',
    description: '',
    location: '',
    start: new Date(),
    end: addHours(new Date(), 1),
    allDay: false,
  });

  const loadCalDAVSettings = useCallback(async () => {
    try {
      const settings = await calDAVService.getAllSettings();
      const activeSettings = settings.filter((s) => s.aktiv);
      setCalDAVSettings(activeSettings);
      if (activeSettings.length > 0 && !selectedSettingId) {
        setSelectedSettingId(activeSettings[0].calDAVSettingID);
      }
    } catch (err) {
      console.error('Error loading CalDAV settings:', err);
      setError('Fehler beim Laden der CalDAV-Einstellungen');
    }
  }, [selectedSettingId]);

  const loadEvents = useCallback(async () => {
    if (!selectedSettingId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await calDAVService.getEvents(selectedSettingId);

      if (response.success) {
        const calendarEvents: CalendarEvent[] = response.events.map((event) => ({
          id: event.uid,
          title: event.summary,
          start: new Date(event.start),
          end: new Date(event.end),
          allDay: event.allDay,
          resource: event,
        }));
        setEvents(calendarEvents);
        setError(null);
      } else {
        setError(response.message || 'Fehler beim Laden der Termine');
      }
    } catch (err: any) {
      console.error('Error loading events:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Termine');
    } finally {
      setLoading(false);
    }
  }, [selectedSettingId]);

  useEffect(() => {
    loadCalDAVSettings();
  }, [loadCalDAVSettings]);

  useEffect(() => {
    if (selectedSettingId) {
      loadEvents();
    }
  }, [selectedSettingId, loadEvents]);

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event.resource);
    setDetailDialogOpen(true);
  };

  const handleNavigate = (newDate: Date) => {
    setCurrentDate(newDate);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleOpenCreateDialog = () => {
    setNewEvent({
      summary: '',
      description: '',
      location: '',
      start: new Date(),
      end: addHours(new Date(), 1),
      allDay: false,
    });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateEvent = async () => {
    if (!selectedSettingId || !newEvent.summary) return;

    try {
      const result = await calDAVService.createEvent({
        calDAVSettingID: selectedSettingId,
        summary: newEvent.summary,
        description: newEvent.description || undefined,
        location: newEvent.location || undefined,
        start: newEvent.start.toISOString(),
        end: newEvent.end.toISOString(),
        allDay: newEvent.allDay,
      });

      if (result.success) {
        setSuccess('Termin erfolgreich erstellt');
        setCreateDialogOpen(false);
        await loadEvents();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Erstellen des Termins');
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedSettingId || !selectedEvent) return;

    try {
      const result = await calDAVService.deleteEvent(selectedSettingId, selectedEvent.uid);

      if (result.success) {
        setSuccess('Termin erfolgreich gelöscht');
        setDetailDialogOpen(false);
        setSelectedEvent(null);
        await loadEvents();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Löschen des Termins');
    }
  };

  // Custom event style
  const eventStyleGetter = () => {
    return {
      style: {
        backgroundColor: '#1976d2',
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  if (loading && calDAVSettings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (calDAVSettings.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Termin Kalender
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Keine aktiven CalDAV-Verbindungen gefunden. Bitte konfigurieren Sie eine CalDAV-Verbindung in den Einstellungen.
        </Alert>
        <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          onClick={() => navigate('/einstellungen')}
        >
          Zu den Einstellungen
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Termin Kalender
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Kalender</InputLabel>
            <Select
              value={selectedSettingId || ''}
              label="Kalender"
              onChange={(e) => setSelectedSettingId(e.target.value as number)}
            >
              {calDAVSettings.map((setting) => (
                <MenuItem key={setting.calDAVSettingID} value={setting.calDAVSettingID}>
                  {setting.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Neuen Termin erstellen">
            <IconButton onClick={handleOpenCreateDialog} color="primary">
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Kalender aktualisieren">
            <IconButton onClick={loadEvents} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="CalDAV-Einstellungen">
            <IconButton onClick={() => navigate('/einstellungen')}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, height: 'calc(100vh - 200px)', minHeight: '600px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          messages={messages}
          culture="de"
          view={view}
          onView={setView}
          date={currentDate}
          onNavigate={handleNavigate}
          eventPropGetter={eventStyleGetter}
        />
      </Paper>

      {/* Event Details Dialog */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon color="primary" />
                {selectedEvent.summary}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                {selectedEvent.description && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Beschreibung
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {selectedEvent.description}
                    </Typography>
                  </>
                )}

                {selectedEvent.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2">{selectedEvent.location}</Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {selectedEvent.allDay
                      ? format(new Date(selectedEvent.start), 'dd.MM.yyyy', { locale: de })
                      : `${format(new Date(selectedEvent.start), 'dd.MM.yyyy HH:mm', { locale: de })} - ${format(new Date(selectedEvent.end), 'HH:mm', { locale: de })}`}
                  </Typography>
                </Box>

                {selectedEvent.allDay && (
                  <Chip label="Ganztägig" size="small" sx={{ mb: 2 }} />
                )}

                {selectedEvent.status && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Status:</strong> {selectedEvent.status}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleDeleteEvent}
                color="error"
                startIcon={<DeleteIcon />}
              >
                Löschen
              </Button>
              <Button onClick={handleCloseDetailDialog}>Schließen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Termin erstellen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Titel"
              value={newEvent.summary}
              onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Beschreibung"
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Ort"
              value={newEvent.location}
              onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newEvent.allDay}
                  onChange={(e) => setNewEvent({ ...newEvent, allDay: e.target.checked })}
                />
              }
              label="Ganztägig"
            />
            <TextField
              label="Start"
              type={newEvent.allDay ? 'date' : 'datetime-local'}
              value={
                newEvent.allDay
                  ? format(newEvent.start, 'yyyy-MM-dd')
                  : format(newEvent.start, "yyyy-MM-dd'T'HH:mm")
              }
              onChange={(e) => setNewEvent({ ...newEvent, start: new Date(e.target.value) })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Ende"
              type={newEvent.allDay ? 'date' : 'datetime-local'}
              value={
                newEvent.allDay
                  ? format(newEvent.end, 'yyyy-MM-dd')
                  : format(newEvent.end, "yyyy-MM-dd'T'HH:mm")
              }
              onChange={(e) => setNewEvent({ ...newEvent, end: new Date(e.target.value) })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Abbrechen</Button>
          <Button
            onClick={handleCreateEvent}
            variant="contained"
            disabled={!newEvent.summary}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentCalendar;
